import type { LucideIcon } from "lucide-react";
import { Users, Clock, UserMinus } from "lucide-react";
import secondselfN8n from "@/assets/case-study/secondself-n8n.png";
import secondselfLogo from "@/assets/logo/secondself.png";

export type Metric = {
  value: string;
  label: string;
  // Optional extras shown on the detail page only.
  icon?: LucideIcon;
  description?: string;
};

export type CaseStudy = {
  slug: string;
  href: string;

  // Card + hero (shared)
  image: string;
  imageAlt: string;
  logo: string;
  logoAlt: string;
  industry: string;
  // Title is split so the hero can render `accent` in gold on its own line.
  // The card concatenates them.
  title: { lead: string; accent: string };
  summary: string;
  metrics: Metric[];

  // Detail page content
  problem: {
    paragraphs: string[];
    pullQuote: string;
    followUp: string[];
  };
  approach: {
    intro: string;
    insightsIntro: string;
    insights: { title: string; body: string }[];
  };
  outcome: {
    intro: string;
    nextSteps: string[];
    paragraphs: string[];
  };
  closingQuote: {
    text: string;
  };
  whyThisMatters: string[];
  cta: {
    headline: string;
    body: string;
    primary: { label: string; href: string };
    secondary: { label: string; href: string };
  };
};

const SECONDSELF: CaseStudy = {
  slug: "secondself",
  href: "/case-study/secondself",

  image: secondselfN8n,
  imageAlt:
    "n8n workflow canvas — AI agent orchestrating onboarding tasks across multiple integrations",
  logo: secondselfLogo,
  logoAlt: "SecondSelf",
  industry: "B2B Services",
  title: {
    lead: "From Bottlenecked Founder",
    accent: "to Client Onboarding in Minutes",
  },
  summary:
    "A founder had hit her ceiling. Onboarding was eating all her time, and each new client made the system worse. We cut it down to minutes.",
  metrics: [
    {
      value: "3x",
      label: "Client capacity",
      icon: Users,
      description:
        "What used to be a hard ceiling opened up once onboarding stopped scaling with each new client.",
    },
    {
      value: "Hours → Min",
      label: "Time per onboarding",
      icon: Clock,
      description:
        "From a multi-hour setup for every new client to a configuration step she can run in minutes.",
    },
    {
      value: "Hire avoided",
      label: "Headcount",
      icon: UserMinus,
      description:
        "The path to scaling no longer required hiring another version of herself to keep up with new clients.",
    },
  ],

  problem: {
    paragraphs: [
      "The client was a founder running a growing marketing agency. Her business produced custom content for each client, with every account requiring its own unique setup. The work was high quality and demand was strong. The founder herself had become the bottleneck.",
      "Every new client meant hours of manual setup. Each account had its own bespoke configuration with six to ten variations of the same underlying processes, all maintained by hand. She was running dozens of duplicate workflows that did essentially the same thing. Any small change had to be propagated across every client's setup individually.",
    ],
    pullQuote:
      "I kept trying to figure out how to make my platform easier to scale, simpler to onboard clients into, and less development-heavy long term, but I kept overcomplicating the solution because I was so ingrained in the build.",
    followUp: [
      "She had been trying to fix this for weeks before reaching out. A contractor was pushing an elaborate rebuild. A full backend migration was being prepared. Every proposed solution made the problem feel larger and the timeline longer.",
    ],
  },

  approach: {
    intro:
      "The audit opened with her business. Where was time going. What was she dreading. What was preventing her from taking on more clients. The technical review came after, once the business picture was clear.",
    insightsIntro: "Three findings shaped the engagement.",
    insights: [
      {
        title: "The duplication wasn't an architecture problem",
        body: "Each client's setup looked unique because of client-specific data baked into the flow. That data could be passed in as a parameter when the workflow ran. One structural change collapsed dozens of duplicate setups into a single shared one.",
      },
      {
        title: "The proposed rebuild was overkill",
        body: "The contractor wanted to build a system that could rewrite itself when external services changed. The reality is that those services rarely change. Weeks of development time, gone, solving a problem that wasn't really happening. The approach was dropped.",
      },
      {
        title: "The backend migration was right, but mis-sequenced",
        body: "Migrating to a new backend before fixing the duplication would have meant carefully porting a broken setup into a clean new home. The cleaner path was to fix the structural issue first using the existing system as a temporary bridge, then migrate one client at a time once the work was already consolidated.",
      },
    ],
  },

  outcome: {
    intro: "The audit ended with four next steps, in priority order:",
    nextSteps: [
      "Restructure the system to consolidate the duplicate setups",
      "Migrate the backend cleanly, one client at a time",
      "Verify by cloning a test client before migrating real ones",
      "Drop the rebuild proposal entirely",
    ],
    paragraphs: [
      "The structural fix took a fraction of the time the founder had been bracing for. New client onboarding moved from a multi-hour build to a configuration step. The path to scaling her client base no longer required hiring another operator to keep up.",
    ],
  },

  closingQuote: {
    text: "He didn't come in trying to sell me some massive rebuild or convince me I needed to scrap everything. Instead, he asked strategic questions that uncovered the actual thing holding the system back... saved me HOURS of development time, HOURS onboarding new clients, and adds a significant amount of money because it allows me to scale faster.",
  },

  whyThisMatters: [
    "Most operators assume their scaling problems require more people, longer hours, or a bigger system. Sometimes that's right. More often the real blocker is a structural decision quietly compounding into duplicated work, manual handoffs, and time the founder no longer has. The cost is paid in their hours, every week, until something gives.",
    "An AI Ops Audit is built to find where AI can take that work off your plate — and to surface the one decision that unlocks the rest.",
  ],

  cta: {
    headline: "Ready to Find Your One Tweak?",
    body: "If your business is growing faster than your time can keep up, an AI Ops Audit can surface where AI fits, what to fix first, and which proposed solutions aren't worth your money.",
    primary: { label: "Get Started", href: "/get-started" },
    secondary: { label: "More Case Studies", href: "/case-study" },
  },
};

export const CASE_STUDIES: CaseStudy[] = [SECONDSELF];

export const getCaseStudyBySlug = (
  slug: string | undefined,
): CaseStudy | undefined => CASE_STUDIES.find((s) => s.slug === slug);
